import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { PreferenceContext } from './context';
import { PreferenceStore } from './preference-store';
import type {
  PreferenceBulkWrite,
  PreferenceProviderProps,
  PreferenceService,
  PreferenceServiceMethod,
  PreferenceWrite,
} from './types';

/**
 * Methods on PreferenceService that accept { Name, Data } and write a single preference.
 * After delegation, we notify subscribers using the Name field.
 */
const SINGLE_WRITE_METHODS = ['set', 'setAtUserLevel', 'setAtAccountLevel', 'setToSession'];

/**
 * Creates a wrapper around the raw preferenceService that intercepts
 * write methods and calls PreferenceStore.notify() after each write.
 * All other properties and methods are delegated as-is.
 * @param {object} preferenceService - The raw PreferenceService instance.
 * @returns {object} Wrapped service with write interception.
 */
const createWrappedService = (preferenceService: PreferenceService): PreferenceService => {
  if (!preferenceService || typeof preferenceService !== 'object') {
    return preferenceService;
  }

  type DynamicPreferenceService = PreferenceService & Record<string, unknown>;
  const source = preferenceService as DynamicPreferenceService;
  const wrapped: DynamicPreferenceService = {};

  const keys = Object.keys(source);
  keys.forEach((key) => {
    const original = source[key];

    if (typeof original !== 'function') {
      Object.defineProperty(wrapped, key, {
        get: () => source[key],
        enumerable: true,
        configurable: true,
      });
      return;
    }

    if (SINGLE_WRITE_METHODS.indexOf(key) >= 0) {
      wrapped[key] = (data: PreferenceWrite, ...rest: unknown[]) => {
        const result = (original as PreferenceServiceMethod).call(source, data, ...rest);
        if (data && data.Name) {
          PreferenceStore.notify(data.Name);
        }
        return result;
      };
    } else if (key === 'bulkCreate') {
      wrapped[key] = (params: PreferenceBulkWrite, ...rest: unknown[]) => {
        const result = (original as PreferenceServiceMethod).call(source, params, ...rest);
        if (params && params.reqs && Array.isArray(params.reqs)) {
          params.reqs.forEach((item) => {
            if (item && item.Name) {
              PreferenceStore.notify(item.Name);
            }
          });
        }
        return result;
      };
    } else {
      wrapped[key] = (...args: unknown[]) => (original as PreferenceServiceMethod).apply(source, args);
    }
  });

  return wrapped;
};

/**
 * PreferenceProvider — drop-in replacement for <PreferenceContext.Provider>.
 *
 * Wraps the raw preferenceService so that write operations automatically
 * notify PreferenceStore subscribers. Components using usePreferences()
 * will re-render when any component writes through the wrapped service.
 *
 * Components using useContext(PreferenceContext) or withPreference get the
 * wrapped service — same API surface, fully backward compatible.
 *
 * @example
 * // Replace:
 * <PreferenceContext.Provider value={preferenceService}>
 *
 * // With:
 * <PreferenceProvider preferenceService={preferenceService}>
 * @returns {React.ReactElement} PreferenceContext.Provider with wrapped service.
 */
export const PreferenceProvider = ({
  preferenceService,
  children
}: PreferenceProviderProps) => {
  const wrappedService = useMemo(
    () => createWrappedService(preferenceService),
    [preferenceService] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <PreferenceContext.Provider value={wrappedService}>
      {children}
    </PreferenceContext.Provider>
  );
};

PreferenceProvider.propTypes = {
  preferenceService: PropTypes.shape({}).isRequired,
  children: PropTypes.node.isRequired,
};
